import { normalizeAdminDateOnly } from "./assessmentAdminRequestedDraftDateHelpers";

/**
 * GET renders this as "October 01, 2026 (2 days)" when the column is empty.
 * It is a label, not a stored DateTime — persisting it on status change would
 * lock in an estimate.
 */
const CALCULATED_COMPLETION_DISPLAY = /\(\s*\d+(?:\.\d+)?\s+days\s*\)\s*$/i;

/**
 * PUT Assessments/Admin binds assessment_completion_date as DateTime.
 * GET returns either "MMMM dd, yyyy" or the calculated label above.
 * @param {string|Date|{ startDate?: string|Date|null }|null|undefined} value
 * @returns {string|null} YYYY-MM-DD or null
 */
export function toAssessmentCompletionDateForUpdate(value) {
  if (
    typeof value === "string" &&
    CALCULATED_COMPLETION_DISPLAY.test(value.trim())
  ) {
    return null;
  }
  return normalizeAdminDateOnly(value);
}

/**
 * Maps camelCase admin Save payload → Assessments/Admin PUT body (snake_case).
 * System-managed dates (first draft uploaded, draft review, final report)
 * are intentionally omitted — BE strips them if sent.
 * published_to_library_on is admin-editable and is sent on Save.
 *
 * @param {object} vqaData
 * @returns {object}
 */
export function buildAdminAssessmentUpdateBody(vqaData) {
  const vqaType =
    vqaData?.vqaType === "true" || vqaData?.vqaType === "onsite"
      ? "onsite"
      : vqaData?.vqaType === "false" || vqaData?.vqaType === "remote"
        ? "remote"
        : null;

  return {
    vqa_location: {
      is_primary_location_required:
        vqaData.is_primary_location_required || null,
      is_secondary_location_required:
        vqaData.is_secondary_location_required || null,
      secondary_location_address: vqaData.secondaryAddress || null,
    },
    other_address: vqaData.otherAddress || null,
    capabilities: vqaData.capabilities || null,
    desired_initial_draft_date: vqaData?.requestDate || null,
    desired_initial_draft_date_change_reason:
      vqaData?.requestedDraftDateChangeReason?.trim() || null,
    additional_info: vqaData.additional_info || null,
    is_multi_sponsor_assessment: vqaData?.multiSponsor,
    is_urgent_report_required: vqaData.is_urgent_report_required || null,
    provider: {
      provider_id: vqaData?.providerId || null,
      provider_type: vqaData.provider_type || null,
      provider_name: vqaData.provider_name || null,
      provider_contact_name: vqaData.providerName || null,
      provider_contact_email: vqaData.providerEmail || null,
      provider_contact_phone: vqaData.providerPhone || null,
      provider_url: vqaData.provider_url || null,
      provider_address1: vqaData?.providerAddress || null,
      provider_city: vqaData.provider_city || null,
      provider_zip: vqaData.provider_zip || null,
      provider_country: vqaData.provider_country || null,
      provider_phone_number: vqaData.provider_phone_number || null,
      provider_additional_addresses:
        vqaData.provider_additional_addresses || null,
      provider_address2: vqaData.provider_address2 || null,
      provider_state: vqaData.provider_state || null,
    },
    vqa_status: vqaData?.vqaStatus || null,
    auditor: {
      vqa_auditor_id: vqaData.auditorId || null,
      vqa_auditor_name: vqaData.auditorName || null,
    },
    peer_reviewer: {
      vqa_auditor_id: vqaData.peerReviewerId || null,
      vqa_auditor_name: vqaData.peerReviewerName || null,
    },
    vqa_requested_on: vqaData.vqa_requested_on || null,
    vqa_requested_by: vqaData.vqa_requested_by || null,
    agreed_initial_draft_date: vqaData?.agreedDraftDate || null,
    assessment_start_date: vqaData.assessment_start_date || null,
    vqa_type: vqaType,
    first_day_of_assessment: vqaData?.firstAssessmentDate || null,
    assessment_completion_date: toAssessmentCompletionDateForUpdate(
      vqaData?.assessmentCompletionDate
    ),
    planning_meeting_completed_on: vqaData?.planningMeetingDate || null,
    first_draft_report_delivered_to_sponsor_on:
      vqaData.firstDraftDelieveredSponsorDate || null,
    draft_report_delivered_to_provider_on:
      vqaData.draftDelieveredToVendorDate || null,
    is_capa_needed: vqaData.capaNeeded,
    capa_delivered_to_sponsor_on: vqaData.capaDelieveredToSponsorDate || null,
    intro_email_date_sent: vqaData?.introEmailDate,
    intro_meeting_complete_date: vqaData?.introMeetingDate,
    intro_meeting_not_required: vqaData?.introMeetingNotRequired ?? null,
    first_draft_delivered_by_auditor_on:
      vqaData?.firstDraftDeliveredByAuditorDate || null,
    final_draft_delivered_by_auditor_on:
      vqaData?.finalDraftDeliveredByAuditorDate || null,
    observation_response_received_on:
      vqaData?.observationResponseReceivedDate || null,
    observation_report_completed_on:
      vqaData?.observationReportCompletedDate || null,
    published_to_library_on: vqaData?.publishedToLibraryDate || null,
    diligent_notes: vqaData?.diligentNotes ?? null,
    provider_contacts: vqaData?.provider_contacts ?? null,
    sponsor_contacts_sponsor_id: vqaData?.sponsor_contacts_sponsor_id ?? null,
    sponsor_contacts: vqaData?.sponsor_contacts ?? null,
    vqa_sponsors: vqaData?.sponsors,
    additional_scope: Array.isArray(vqaData?.additional_scope)
      ? vqaData.additional_scope
      : null,
  };
}
